import React, {
  type ReactElement,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button } from "reactstrap";
import {
  type InjectedFormProps,
  SubmissionError,
  formValueSelector,
  reduxForm,
  reset,
} from "redux-form";
import type { ThunkDispatchType } from "src/app/reducers/IMainAppState";
import Loader from "src/shared/components/Loader";
import { FileInputOld } from "src/shared/forms/file/inputs/FileInputOld";
import { FileUploadRendererContext } from "src/shared/forms/formrenderers/FileUploadRenderer";
import { InputRendererContext } from "src/shared/forms/formrenderers/InputRenderer";

const FILE = "file";

interface IFileUploadFormData {
  [FILE]?: FileList;
}

interface IFileUploadFormInnerProps
  extends IFileUploadFormProps,
    InjectedFormProps<IFileUploadFormData, IFileUploadFormProps> {}

const FileUploadFormInner = (props: IFileUploadFormInnerProps) => {
  const {
    submitLabel,
    inputLabel,
    form,
    pristine,
    submitting,
    validationDisabled,
    children,
    tooltip,
  } = props;

  // Get file value.
  const file: FileList | undefined = useSelector((state) =>
    formValueSelector(form)(state, FILE),
  );

  const dispatch = useDispatch<ThunkDispatchType>();

  const [uploadPromise, setUploadPromise] = useState(undefined);
  const [progressEvent, setProgressEvent] = useState(undefined);

  const formRef = useRef<HTMLDivElement | null>(null);

  // If file is set to undefined, update the dom and clean the value.
  useEffect(() => {
    if (file === undefined && formRef.current) {
      const fileInputElement =
        formRef.current.querySelector<HTMLInputElement>('input[type="file"]');
      if (fileInputElement) {
        fileInputElement.value = "";
      }
    }
  }, [file]);

  const { onUploadFile, onFileChange } = props;
  const handleFileUpload = useCallback(
    async (formData: IFileUploadFormData) => {
      const handleUploadProgress = setProgressEvent;

      const promise = (async () => {
        try {
          const url = await onUploadFile(
            formData[FILE]!.item(0),
            handleUploadProgress,
          );

          onFileChange(url);

          // reset value.
          dispatch(reset(form));

          return url;
        } catch (ex) {
          if (ex instanceof SubmissionError) {
            throw ex;
          }
          throw new SubmissionError({
            _error: ex,
          });
        } finally {
          // Remove progress bar.
          setProgressEvent(undefined);
        }
      })();

      setUploadPromise(promise);

      await promise;
    },
    [onUploadFile, onFileChange, dispatch, form],
  );

  return (
    <div ref={formRef}>
      <FileUploadRendererContext.Provider value={progressEvent}>
        <InputRendererContext.Provider value={{ form }}>
          <FileInputOld
            inputID={FILE}
            label={inputLabel}
            validationDisabled={
              uploadPromise !== undefined || validationDisabled
            }
            tooltip={tooltip}
          />
        </InputRendererContext.Provider>
      </FileUploadRendererContext.Provider>

      {children}

      <p>
        <Button
          type="button"
          color="primary"
          onClick={props.handleSubmit(handleFileUpload)}
          disabled={!file || file.length === 0 || pristine || submitting}
        >
          {submitLabel}
        </Button>
      </p>

      {uploadPromise && (
        <Loader promise={uploadPromise} loadingStatusWrapper={() => null} />
      )}
    </div>
  );
};

export interface IFileUploadFormProps {
  inputLabel: ReactElement;
  submitLabel: ReactElement;
  onFileChange: (fileUrl: string) => void;
  onUploadFile: (
    file: File,
    handleUploadProgress?: (progressEvent: ProgressEvent) => void,
  ) => Promise<string>;
  validationDisabled?: boolean;
  children?: ReactNode;
  tooltip?: ReactNode;
}

export const FileUploadForm = reduxForm<
  IFileUploadFormData,
  IFileUploadFormProps
>({
  form: "FileUploadForm",
})(FileUploadFormInner);

export default FileUploadForm;
